<template>
  <v-dialog v-model="dialog" max-width="80vw">
    <v-card>
      <v-card-title class="text-h5 bg-primary-lighten-1 align-center d-flex justify-space-between" primary-title>
        <div>
          <v-icon size="large" style="color: white" class="pr-3">mdi-information</v-icon>
          <span style="color: white">Filter-Kriterien festlegen</span>
        </div>
        <Button large class="close-button" icon @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </Button>
      </v-card-title>
      <v-card-text>
        <h4 class="pt-4">Nach Merkmalen filtern:</h4>
        <v-chip-group v-model="specialFeaturesSelected" multiple column>
          <v-chip color="primary" filter variant="outlined" v-for="(item, key, index) in specialFeatures" :key="index">
            {{ item.label }}
          </v-chip>
        </v-chip-group>

        <v-divider class="mt-4" />
        <h4 class="pt-4">Nach Einrichtungsart filtern:</h4>
        <v-select
            v-model="einrichtungsartSelected"
            :items="gefilterteEinrichtungsarten"
            item-title="name"
            item-value="id"
            label="Einrichtungsarten auswählen"
            clearable
            single-line
            multiple
            chips
        />

        <v-divider class="pb-4" />
        <h4 class="pt-4">Nach Datum filtern:</h4>
        <v-checkbox :label="`nach Datum filtern`" v-model="filterByDate" color="primary"></v-checkbox>

        <v-row>
          <v-col cols="12">
            <v-btn :disabled="!filterByDate" @click="addNewDateFilter" color="primary" class="mb-4">
              <span>Datumsfilter hinzufügen</span>
            </v-btn>
          </v-col>
        </v-row>

        <DatePickerDialog ref="datePickerDialog" @emitDate="changeDate"></DatePickerDialog>

        <div v-for="(filter, index) in dateFilters" :key="index">
          <v-row>
            <v-col cols="5">
              <v-select density="compact" v-model="filter.selectedDateType" :items="dateTypes"
                :disabled="!filterByDate" label="Datumstyp auswählen"></v-select>
            </v-col>

            <v-col cols="3">
              <v-btn :disabled="!filterByDate" block @click="openDatePickerDialog(index, 'startDate')" color="primary">
                <span v-if="filter.startDate">{{ formatDate(filter.startDate) }}</span>
                <span v-else>JJJJ.MM.TT</span>
                <v-icon end>mdi-clock</v-icon>
              </v-btn>
            </v-col>

            <v-col cols="3">
              <v-btn :disabled="!filterByDate" block @click="openDatePickerDialog(index, 'endDate')" color="primary">
                <span v-if="filter.endDate">{{ formatDate(filter.endDate) }}</span>
                <span v-else>JJJJ.MM.TT</span>
                <v-icon end>mdi-clock</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="1">
              <v-btn :disabled="!filterByDate" density="compact" @click="removeDateFilter(index)" color="red" icon="mdi-delete"></v-btn>
            </v-col>
          </v-row>
        </div>
        
        <v-divider class="pb-4" />
        <h4 style="display: inline">Nach Alter filtern: </h4>
        <Tooltip top style="display: inline">
          <v-icon size="small" class="pointer" color="grey">mdi-information</v-icon>
          <template #content>
            <span>Kindesalter zum Zeitpunkt des Aufnahmedatums</span>
          </template>
        </Tooltip>
        <v-checkbox :label="`nach Alter filtern`" v-model="filterByAge" class="pb-0 pt-0" color="primary"></v-checkbox>

        <h4>Minimales Alter:</h4>
        <v-slider color="primary" class="mt-0 ml-2" :disabled="!filterByAge" v-model="minAge" min="0" max="12"
          thumb-label :label="`Jahre: ${minAge}`" prepend-icon="mdi-calendar" step="1" />
        <v-slider color="primary-lighten-2" class="mt-0 ml-2" :disabled="!filterByAge" v-model="minMonth" min="0"
          max="11" thumb-label prepend-icon="mdi-calendar" :label="`Monate: ${minMonth}`" step="1" />

        <h4>Maximales Alter:</h4>
        <v-slider color="primary" class="mt-0 ml-2" :disabled="!filterByAge" v-model="maxAge" min="0" max="12"
          thumb-label :label="`Jahre: ${maxAge}`" prepend-icon="mdi-calendar" step="1" />
        <v-slider color="primary-lighten-2" class="mt-0 ml-2" :disabled="!filterByAge" v-model="maxMonth" min="0"
          max="11" thumb-label :label="`Monate: ${maxMonth}`" prepend-icon="mdi-calendar" step="1" />

        <v-divider />
        <h4 class="pt-4">Nach Status filtern:</h4>
        <div style="display: flex">
          <div v-for="(item, key, index) in registrationStatus" :key="index">
            <v-switch :label="item.label" v-model="item.value" color="primary" class="px-4"></v-switch>
          </div>
        </div>
        <v-divider />
        <div v-for="(kita, index) in einrichtungenSelected" :key="index">
          <h4 class="pt-2">{{ kita.name }}</h4>
          <v-switch v-model="einrichtungenSelected[index].chooseSpecificPrio" :label="`Nach spezifischer Priorität filtern`"
            color="primary"></v-switch>
          <h4 class="text-primary" v-if="kita.chooseSpecificPrio">
            Spezifische Priorität: Es werden nur die Anmeldungen angezeigt, bei der die Einrichtung mit der
            spezifizierten Priorität von den Erziehungsberechtigten priorisiert wurde.
          </h4>
          <div style="display: flex" v-if="kita.chooseSpecificPrio">
            <div v-for="(p, i) in kita.prios" :key="i">
              <v-switch :label="`Priorität ${i + 1}`" v-model="einrichtungenSelected[index].prios[i]" class="px-4"
                color="primary"></v-switch>
            </div>
          </div>
          <v-divider />
        </div>
          <h4 class="pt-4">Nach Schule filtern:</h4>
          <v-checkbox
            :label="`nach Schule filtern`"
            v-model="filterBySchoolProperties"
            color="primary"
          />

          <v-row>
            <v-col cols="12" md="3">
              <v-select
                v-model="schoolKindSelected"
                :items="schoolKindOptions"
                item-title="title"
                item-value="value"
                label="Schulkind"
                :disabled="!filterBySchoolProperties"
                clearable
              />
            </v-col>

            <v-col cols="12" md="5">
              <v-select
                v-model="schoolSelected"
                :items="gefilterteSchulen"
                label="Schule auswählen"
                :disabled="!filterBySchoolProperties || schoolKindSelected !== 'JA'"
                clearable
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="schoolClassInput"
                label="Klasse eingeben"
                :disabled="!filterBySchoolProperties || schoolKindSelected !== 'JA'"
                clearable
                hint="z. B. 1, 1A, 4d"
                persistent-hint
              />
            </v-col>
          </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <Button raised variant="flat" color="primary" @click="confirm">
          Mit diesen Einstellungen filtern
        </Button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Tooltip from '@s/views/general_components/components/Tooltip'
import Button from '@s/views/general_components/components/Button'
import DatePickerDialog from '../../../../general_components/dialogs/DatePickerDialog.vue'
import { formatDateToISOString, formatDate } from '@s/util/helper.js'

export default {
  name: "filter-dialog",
  components: {
    Tooltip,
    Button,
    DatePickerDialog
  },
  data() {
    return {
      dialog: false,
      einrichtungsarten: this.$select('general.einrichtungsarten as einrichtungsarten'),
      einrichtungsartSelected: [],
      einrichtungenSelected: [],
      filterByAge: false,
      filterByDate: false,

      filterBySchoolProperties: false,
      schoolKindSelected: null,
      schoolSelected: null,
      schoolClassInput: '',

      minAge: 0,
      maxAge: 1,
      minMonth: 0,
      maxMonth: 1,
      specialFeaturesSelected: [],
      dates: {
        aufnahmedatum: {
          startDate: null,
          endDate: null
        },
        altAufnahmedatum: {
          startDate: null,
          endDate: null
        },
        eingangAnmeldung: {
          startDate: null,
          endDate: null
        },
        letzteAenderung: {
          startDate: null,
          endDate: null
        },
        letzteBenachrichtigung: {
          startDate: null,
          endDate: null
        }
      },
      selectedDateType: null,
      dateTypes: [
        { title: 'Aufnahmedatum', value: 'aufnahmedatum' },
        { title: 'Alternatives Aufnahmedatum', value: 'altAufnahmedatum' },
        { title: 'Eingang der Anmeldung', value: 'eingangAnmeldung' },
        { title: 'Letzte Änderung', value: 'letzteAenderung' },
        { title: 'Letzte Benachrichtigung', value: 'letzteBenachrichtigung' }
      ],
      dateFilters: [],
      specialFeatures: [{
        label: 'Integrationsbedarf',
        value: false,
        searchString: 'INTEGRATIONSBEDARF'
      }, {
        label: 'Bemerkung vorhanden',
        value: false,
        searchString: 'BEMERKUNG'
      }, {
        label: 'Unterjährig',
        value: false,
        searchString: 'UNTERJAEHRIG'
      }, {
        label: 'Alleinerziehend',
        value: false,
        searchString: 'SINGLEPARENT'
      }, {
        label: 'Geschwisterkind',
        value: false,
        searchString: 'SIBLINGS'
      }, {
        label: 'Alle KBEs haben abgelehnt',
        value: false,
        searchString: 'ALLREJECTED'
      }, {
        label: 'Alternatives Aufnahmedatum',
        value: false,
        searchString: 'ALTERNATIVEDATE'
      }, {
        label: 'Interne Dringlichkeit',
        value: false,
        searchString: 'INTERNALPRIO'
      }, {
        label: 'Sie sind mit der Zuteilung dran',
        value: false,
        searchString: 'FACILITYROLEMUSTACT'
      }, {
        label: 'Alle Vertreter sind berufstätig',
        value: false,
        searchString: 'ALL_VERTRETER_BERUFSTAETIG'
      }, {
        label: 'Mindestens ein Vertreter ist berufstätig',
        value: false,
        searchString: 'AT_LEAST_ONE_VERTRETER_BERUFSTAETIG'
      }, {
        label: 'Neuer Hauptwohnsitz',
        value: false,
        searchString: 'AT_LEAST_ONE_NEUER_HAUPTWOHNSITZ'
      }, {
        label: 'Elternzeit',
        value: false,
        searchString: 'ELTERNZEIT',
      }, {
        label: 'Ferienbetreuung',
        value: false,
        searchString: 'FERIENBETREUUNG'
      }
      ],
      registrationStatus: [{
        label: 'Offen',
        value: false,
        searchString: 'OFFEN'
      }, {
        label: 'Genehmigt',
        value: false,
        searchString: 'GENEHMIGT'
      }, {
        label: 'Zurückgestellt',
        value: false,
        searchString: 'ZURUECKGESTELLT'
      }]
    }
  },
  computed: {
    gefilterteEinrichtungsarten() {
      const user = this.$select('user.value');
      const userAgs = user?.ags || [];

      const einrichtungen = this.$select('general.einrichtungen');
      const einrichtungsarten = this.$select('general.einrichtungsarten');

      if (!einrichtungen?.length || !einrichtungsarten?.length) {
        return einrichtungsarten || [];
      }

      const allowedArtIds = new Set(
        einrichtungen
          .filter(e => userAgs.includes(e.ags))
          .map(e => e.einrichtungsart)
      );

      return einrichtungsarten.filter(a => allowedArtIds.has(a.id));
    },
    gefilterteSchulen() {
      const slice = this.$select('bedarfsAnmeldung');
      const anmeldungen = slice?.bedarfsAnmeldungen || [];
      const schulMap = new Map();

      anmeldungen.forEach(anmeldung => {
        (anmeldung.kindesdaten || []).forEach(kind => {
          if (kind.schulkind === true && kind.schule && kind.schule.trim() !== '') {
            const original = kind.schule.trim();
            const normalized = original.toUpperCase().replace(/\s+/g, ' ');

            if (!schulMap.has(normalized)) {
              schulMap.set(normalized, original);
            }
          }
        });
      });

      return Array.from(schulMap.values()).sort((a, b) => a.localeCompare(b, 'de'));
    },

    schoolKindOptions() {
      return [
        { title: 'Ja', value: 'JA' },
        { title: 'Nein', value: 'NEIN' }
      ];
    }
  },
  watch: {
    dialog(val) {
      val || this.close();
    },
    minAge(newAge){
      if(newAge > this.maxAge){
        this.maxAge = newAge + 1;
      }
    },
    maxAge(newAge){
      if(newAge < this.minAge){
        this.minAge = newAge - 1;
      }
    },
    minMonth(newMonth){
      if(this.minAge === this.maxAge){
        if(newMonth > this.maxMonth){
          this.maxMonth = newMonth + 1;
        }
      }
    },
    maxMonth(newMonth){
      if(this.minAge === this.maxAge){
        if(newMonth < this.minMonth){
          this.minMonth = newMonth - 1;
        }
      }
    }
  },
  methods: {
    formatDate,
    normalizeSchoolClass(value) {
      if (!value) return '';
      return String(value).trim().toUpperCase().replace(/\s+/g, '');
    },
    extractSchoolClassNumber(value) {
      const normalized = this.normalizeSchoolClass(value);
      const match = normalized.match(/^\d+/);
      return match ? match[0] : normalized;
    },
    updateStartDate(dateProperty, startEnd, newValue) {
      const parsedDate = this.parseDate(newValue);
      if(startEnd === 'startDate'){
        this.dates[dateProperty].startDate = parsedDate;
      }
      if(startEnd === 'endDate'){
        this.dates[dateProperty].endDate = parsedDate;
      }
    },
    parseDate(value) {
      const [day, month, year] = value.split('.');
      return new Date(`${year}-${month}-${day}`);
    },
    addNewDateFilter() {
      this.dateFilters.push({
        selectedDateType: this.dateTypes[0].value,
        startDate: null,
        endDate: null
      });
    },
    removeDateFilter(index) {
      // Reset the filter values before removing
      const filter = this.dateFilters[index];
      filter.startDate = null;
      filter.endDate = null;
      filter.selectedDateType = null;
      this.dateFilters.splice(index, 1);
    },
    openDatePickerDialog(typeOrIndex, dateType, date = null) {
      if (typeof typeOrIndex === 'string') {
        // 1. Alternative
        const title = this.getDateTypeTitle(typeOrIndex);
        const key = `${typeOrIndex}${dateType.charAt(0).toUpperCase() + dateType.slice(1)}`;
        this.$nextTick(() => {
          this.$refs.datePickerDialog.open(title, key, date);
        });
      } else {
        // 2. Alternative
        const index = typeOrIndex;
        const filter = this.dateFilters[index];
        if (filter && filter.selectedDateType) {
          const title = this.getDateTypeTitle(filter.selectedDateType);
          const key = `${filter.selectedDateType}${dateType.charAt(0).toUpperCase() + dateType.slice(1)}`;
          const selectedDate = filter[dateType];
          this.$nextTick(() => {
            this.$refs.datePickerDialog.open(title, key, selectedDate);
          });
        } else {
          console.warn(`Filter or selectedDateType not defined for index ${index}`);
        }
      }
    },
    getDateTypeTitle(value) {
      const dateType = this.dateTypes.find(type => type.value === value);
      return dateType ? dateType.title : '';
    },
    changeDate(key, date) {
      const type = key.replace('StartDate', '').replace('EndDate', '');
      
      // 1. Alternative
      if (this.dates[type]) {
        if (key.includes('StartDate')) {
          this.dates[type].startDate = date;
        } else if (key.includes('EndDate')) {
          this.dates[type].endDate = date;
        }
      }
      
      // 2. Alternative
      for (const filter of this.dateFilters) {
        if (filter.selectedDateType && key.includes(filter.selectedDateType)) {
          if (key.includes('StartDate')) {
            filter.startDate = date;
          } else if (key.includes('EndDate')) {
            filter.endDate = date;
          }
        }
      }
    },
    open(einrichtungenSelected, reset = false) {
      this.einrichtungenSelected = [];
      einrichtungenSelected.forEach(einrichtung => {
        let prios = [false, false, false, false, false];
        this.einrichtungenSelected.push({
          id: einrichtung.id,
          name: einrichtung.name,
          chooseSpecificPrio: false,
          prios: prios
        })
      })
      if(reset){
        this.specialFeaturesSelected = [];
        this.specialFeatures.forEach(item => {
          item.value = false;
        });
        this.filterByAge = false;
        this.filterByDate = false;

        this.filterBySchoolProperties = false;
        this.schoolKindSelected = null;
        this.schoolSelected = null;
        this.schoolClassInput = '';

        this.minAge = 0;
        this.maxAge = 1;
        this.registrationStatus.forEach(item => {
          item.value = false;
        })
        this.einrichtungsartSelected = [];

        for (const dateCategory in this.dates) {
          if (this.dates.hasOwnProperty(dateCategory)) {
            this.dates[dateCategory].startDate = null;
            this.dates[dateCategory].endDate = null;
          }
        }
        //this.dateFilters = [];
      }
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    confirm() {
      let returnString = '';

      // Einrichtungsarten and priorities
      this.einrichtungenSelected.forEach(kita => {
        if (kita.chooseSpecificPrio) {
          for (let i = 0; i < kita.prios.length; ++i) {
            if (kita.prios[i]) {
              returnString += `${kita.id}-PRIO:${i + 1}, `;
            }
          }
        } else {
          returnString += `${kita.id}, `;
        }
      });

      // Special features
      this.specialFeaturesSelected.forEach(index => {
        returnString += `${this.specialFeatures[index].searchString}, `;
      });

      // Registration status
      this.registrationStatus.forEach(status => {
        if (status.value) {
          returnString += `${status.searchString}, `;
        }
      });

      // Age filter
      if (this.filterByAge) {
        if (this.minAge <= this.maxAge) {
          for (let i = this.minAge * 12 + this.minMonth; i <= this.maxAge * 12 + this.maxMonth; ++i) {
            returnString += `$$AGE-${i}, `;
          }
        }
      }

      // Einrichtungsart
      if (this.einrichtungsartSelected.length > 0) {
        returnString += `EINRICHTUNGSART-${this.einrichtungsartSelected}, `;
      }

      // Date types
      if (this.filterByDate) {
        this.dateFilters.forEach(filter => {
          const startDate = filter.startDate;
          const endDate = filter.endDate;
          const selectedType = filter.selectedDateType;
          if (startDate && endDate && selectedType) {
            const startDateISO = formatDateToISOString(startDate);
            const endDateISO = formatDateToISOString(endDate);
            returnString += `${selectedType.toUpperCase()}-${startDateISO}-${endDateISO}, `;
          }
        });
      }

      // Schuleigenschaften
      if (this.filterBySchoolProperties && this.schoolKindSelected) {
        returnString += `SCHULKIND-${this.schoolKindSelected}, `;

        if (this.schoolKindSelected === 'JA') {
          if (this.schoolSelected) {
            returnString += `SCHULE-${this.schoolSelected}, `;
          }

          const normalizedClass = this.normalizeSchoolClass(this.schoolClassInput);
          if (normalizedClass) {
            if (/^\d+$/.test(normalizedClass)) {
              returnString += `SCHULKLASSE-NUM-${normalizedClass}, `;
            } else {
              returnString += `SCHULKLASSE-${normalizedClass}, `;
            }
          }
        }
      }


      this.$emit('returnSearchString', returnString);
      this.close();
    }
  }
}
</script>
